import { Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { looseListT } from '@/i18n/looseT';

import { PublicDocLayout } from './PublicDocLayout';
import { useDocumentTitle } from './useDocumentTitle';

/**
 * `/user-terms` — HAi Creative 服務條款與隱私權保護政策(公開頁,不包 `RequireAuth`)
 * -----------------------------------------------------------------------------
 * 路徑對齊 mentor(`mentor-login.haitech.com.tw/user-terms`),owner 2026-09-11 指定。
 * 文案逐字來自 owner 交付的條款稿(2026-09-14 改版),**不改寫**;要動文案回那份稿子取。
 *
 * 🔴 **owner 2026-09-14/15 拍板:法務的文字一字一句、包含標點符號都不能改。**
 * 所以這兩份文件的標點是**全形**(`，（）；：`),跟 `site.json` 其他段落(官網首頁 / 頁尾,
 * 半形)**刻意不一致** —— 那不是漏正規化,是原文不動。已知且刻意留著的用語不一致:
 * 責任主體「本服務」/「本公司」混用(六、3「如致本服務受有損害」vs 隱私一「如致本公司
 * 受有損害」)、《免責聲明》書名號時有時無(一、3 與七、4 有 / 八沒有)、第九條標題與
 * 獨立文件《免責聲明》同名、術語「生成內容」(本檔)vs「生成素材」(免責聲明)。
 * **不要順手統一** —— 有機械閘擋著(`siteCompleteness.test` 的「法務文字一字一句…」那條)。
 *
 * ⚠️ **章節編號寫在 h3 的文案裡(「七、AI 生成內容」)而不是由 CSS counter 產生** ——
 * 條文本體會互相引用(第八條寫「依第七條約定授權會員使用之生成內容」),編號必須看得見
 * 而且必須跟稿子一致。把它改成自動編號 = 只要有人插一節,所有交叉引用就一起失準,
 * 而畫面上完全正常。
 *
 * ⚠️ **目錄分成兩組、各自從 01 起算**(`tocTerms` / `tocPrivacy`):兩部分在條文裡
 * 各自從「一、」重新編號,目錄若連號 01–21,第二部分的 13 會對到條文的「一、」——
 * 讀者照目錄號碼找不到條文,而目錄本身看起來完全正常。
 *
 * ⚠️ Google 政策那兩條含外部連結(`<policy>` / `<perm>` 自訂標籤走 `<Trans>`)——
 * 連結是法遵要求的一部分(Limited Use 揭露),不可以拆成純文字。
 */

/** 第一部分(服務條款)十二節;順序即目錄順序,與 `terms.tocTerms` 等長。 */
const TERMS_IDS = [
  's1', 's2', 's3', 's4', 's5', 's6', 's7', 's8', 's9', 's10', 's11', 's12',
] as const;
/** 第二部分(隱私權保護政策)九節;與 `terms.tocPrivacy` 等長。 */
const PRIVACY_IDS = ['p1', 'p2', 'p3', 'p4', 'p5', 'p6', 'p7', 'p8', 'p9'] as const;

const GOOGLE_POLICY_URL = 'https://developers.google.com/terms/api-services-user-data-policy';
const GOOGLE_PERMISSIONS_URL = 'https://myaccount.google.com/permissions';

export function UserTermsPage() {
  const { t } = useTranslation('site');
  // 條款子項清單走共用的 `looseListT`,見 `i18n/looseT.ts`
  const list = looseListT(t);
  useDocumentTitle(t('terms.docTitle'));

  /** 目錄的一組(部分標題 + 該部分的錨點清單);兩組各自從 01 起算。 */
  const tocGroups = [
    { label: t('terms.partTermsTitle'), itemsKey: 'terms.tocTerms', ids: TERMS_IDS },
    { label: t('terms.partPrivacyTitle'), itemsKey: 'terms.tocPrivacy', ids: PRIVACY_IDS },
  ] as const;

  return (
    <PublicDocLayout
      scope="hai-site--terms"
      pageLabel={t('terms.barPage')}
      title={t('terms.title')}
      updatedDate={t('terms.updatedDate')}
      footerSecondary={{ to: '/disclaimer', label: t('docFooter.disclaimer') }}
    >
      <nav className="toc" aria-label={t('terms.tocTitle')}>
        <h2>{t('terms.tocTitle')}</h2>
        {tocGroups.map((g) => (
          <Fragment key={g.itemsKey}>
            <div className="toc-part">{g.label}</div>
            <ol>
              {list(g.itemsKey).map((label, i) => (
                <li key={g.ids[i]}>
                  <a href={`#${g.ids[i]}`}>
                    <span className="no">{String(i + 1).padStart(2, '0')}</span>
                    {label}
                  </a>
                </li>
              ))}
            </ol>
          </Fragment>
        ))}
      </nav>

      {/* ================= 第一部分 服務條款 ================= */}
      <div className="part">
        <span className="tag">{t('terms.partTermsTag')}</span>
        <h2>{t('terms.partTermsTitle')}</h2>
      </div>

      <h3 id="s1">{t('terms.s1.title')}</h3>
      <ol>
        <li>{t('terms.s1.i1')}</li>
        <li>
          {t('terms.s1.i2')}
          <ul>
            {list('terms.s1.i2sub').map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </li>
        <li>{t('terms.s1.i3')}</li>
      </ol>

      <h3 id="s2">{t('terms.s2.title')}</h3>
      <ol>
        {list('terms.s2.items').map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ol>

      <h3 id="s3">{t('terms.s3.title')}</h3>
      <ol>
        <li>{t('terms.s3.i1')}</li>
        <li>
          {t('terms.s3.i2')}
          <ul>
            {list('terms.s3.i2sub').map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </li>
        <li>{t('terms.s3.i3')}</li>
        <li>{t('terms.s3.i4')}</li>
      </ol>

      <h3 id="s4">{t('terms.s4.title')}</h3>
      <p>{t('terms.s4.lead')}</p>
      <ul>
        {list('terms.s4.items').map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>

      <h3 id="s5">{t('terms.s5.title')}</h3>
      <ol>
        <li>{t('terms.s5.i1')}</li>
        <li>
          {t('terms.s5.i2')}
          <ul>
            {list('terms.s5.i2sub').map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </li>
        <li>{t('terms.s5.i3')}</li>
      </ol>

      <h3 id="s6">{t('terms.s6.title')}</h3>
      <ol>
        {list('terms.s6.items').map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ol>

      <h3 id="s7">{t('terms.s7.title')}</h3>
      <ol>
        {list('terms.s7.items').map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ol>

      <h3 id="s8">{t('terms.s8.title')}</h3>
      <p>{t('terms.s8.body')}</p>

      <h3 id="s9">{t('terms.s9.title')}</h3>
      <p>{t('terms.s9.lead')}</p>
      <ul>
        {list('terms.s9.items').map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>
      <p>{t('terms.s9.tail')}</p>

      <h3 id="s10">{t('terms.s10.title')}</h3>
      <p>{t('terms.s10.body')}</p>

      <h3 id="s11">{t('terms.s11.title')}</h3>
      <p>{t('terms.s11.body')}</p>

      <h3 id="s12">{t('terms.s12.title')}</h3>
      <p>{t('terms.s12.lead')}</p>
      <ul>
        {list('terms.s12.items').map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>

      {/* ================= 第二部分 隱私權保護政策 ================= */}
      <div className="part">
        <span className="tag">{t('terms.partPrivacyTag')}</span>
        <h2>{t('terms.partPrivacyTitle')}</h2>
      </div>

      <h3 id="p1">{t('terms.p1.title')}</h3>
      <p>{t('terms.p1.body')}</p>

      <h3 id="p2">{t('terms.p2.title')}</h3>
      <p>{t('terms.p2.body')}</p>
      <p>{t('terms.p2.body2')}</p>

      <h3 id="p3">{t('terms.p3.title')}</h3>
      <p>{t('terms.p3.lead')}</p>
      <ul>
        {list('terms.p3.items').map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>
      <p>{t('terms.p3.tail')}</p>

      <h3 id="p4">{t('terms.p4.title')}</h3>
      <ol>
        <li>{t('terms.p4.i1')}</li>
        <li>
          {t('terms.p4.i2')}
          <ul>
            {list('terms.p4.i2sub').map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </li>
        <li>{t('terms.p4.i3')}</li>
        <li>{t('terms.p4.i4')}</li>
      </ol>

      <h3 id="p5">{t('terms.p5.title')}</h3>
      <p>{t('terms.p5.body')}</p>

      <h3 id="p6">{t('terms.p6.title')}</h3>
      <p>{t('terms.p6.body')}</p>

      <h3 id="p7">{t('terms.p7.title')}</h3>
      <ol>
        <li>{t('terms.p7.i1')}</li>
        <li>{t('terms.p7.i2')}</li>
        <li>
          <Trans
            t={t}
            i18nKey="terms.p7.i3"
            components={{
              policy: (
                <a href={GOOGLE_POLICY_URL} target="_blank" rel="noopener noreferrer">
                  {}
                </a>
              ),
            }}
          />
        </li>
        <li>
          <Trans
            t={t}
            i18nKey="terms.p7.i4"
            components={{
              perm: (
                <a href={GOOGLE_PERMISSIONS_URL} target="_blank" rel="noopener noreferrer">
                  {}
                </a>
              ),
            }}
          />
        </li>
      </ol>

      <h3 id="p8">{t('terms.p8.title')}</h3>
      <p>{t('terms.p8.lead')}</p>
      <ul>
        {list('terms.p8.items').map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>

      <h3 id="p9">{t('terms.p9.title')}</h3>
      <p>{t('terms.p9.body')}</p>
    </PublicDocLayout>
  );
}
